import React, { useState, useEffect, useContext } from 'react';
import { AppContext } from '../context/AppContext';
import { PlusIcon, PencilIcon, TrashIcon, TagIcon } from '@heroicons/react/24/outline';

const ManageStatuses = () => {
  const { token } = useContext(AppContext);
  const [tags, setTags] = useState([]);
  const [selectedTag, setSelectedTag] = useState(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [formData, setFormData] = useState({ name: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Fetch service tags (categoryId=2 for Services)
  useEffect(() => {
    fetchTags();
  }, [token]);

  const fetchTags = async () => {
    try {
      const response = await fetch('/api/statuses?categoryId=2', {
        headers: { Authorization: token },
      });
      if (response.ok) {
        setTags(await response.json());
      }
    } catch (error) {
      console.error('Error fetching tags:', error);
    }
  };

  const handleOpenForm = (tag = null) => {
    if (tag) {
      setSelectedTag(tag);
      setFormData({ name: tag.name });
    } else {
      setSelectedTag(null);
      setFormData({ name: '' });
    }
    setIsFormOpen(true);
    setError('');
  };

  const handleCloseForm = () => {
    setIsFormOpen(false);
    setSelectedTag(null);
    setFormData({ name: '' });
    setError('');
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const url = selectedTag
        ? `/api/statuses/${selectedTag.id}`
        : '/api/statuses';
      const method = selectedTag ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
        body: JSON.stringify({
          name: formData.name,
          categoryId: 2, // Services category
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to save tag');
      }

      const savedTag = await response.json();

      if (selectedTag) {
        setTags((prev) =>
          prev.map((t) => (t.id === savedTag.id ? savedTag : t))
        );
      } else {
        setTags((prev) => [...prev, savedTag]);
      }

      handleCloseForm();
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this tag? It will be removed from all services.')) return;

    try {
      const response = await fetch(`/api/statuses/${id}`, {
        method: 'DELETE',
        headers: { Authorization: token },
      });

      if (response.ok) {
        setTags((prev) => prev.filter((tag) => tag.id !== id));
      } else {
        const data = await response.json();
        alert(data.error || 'Failed to delete tag');
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Failed to delete tag');
    }
  };

  return (
    <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Manage Service Tags</h1>
          <p className="mt-2 text-sm text-gray-700">
            Create and manage tags that can be applied to services for categorization and filtering.
          </p>
        </div>
        <div className="mt-4 sm:mt-0">
          <button
            type="button"
            onClick={() => handleOpenForm()}
            className="inline-flex items-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
          >
            <PlusIcon className="-ml-0.5 mr-1.5 h-5 w-5" aria-hidden="true" />
            Add Tag
          </button>
        </div>
      </div>

      {/* Tag List */}
      <div className="mt-8">
        {tags.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg shadow ring-1 ring-gray-200">
            <TagIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-semibold text-gray-900">No tags</h3>
            <p className="mt-1 text-sm text-gray-500">
              Get started by creating a new tag for services.
            </p>
            <div className="mt-6">
              <button
                type="button"
                onClick={() => handleOpenForm()}
                className="inline-flex items-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500"
              >
                <PlusIcon className="-ml-0.5 mr-1.5 h-5 w-5" aria-hidden="true" />
                Add Tag
              </button>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow ring-1 ring-gray-200">
            <ul role="list" className="divide-y divide-gray-100">
              {tags.map((tag) => (
                <li
                  key={tag.id}
                  className="flex items-center justify-between gap-x-6 px-4 py-4 hover:bg-gray-50"
                >
                  <div className="min-w-0 flex items-center gap-3">
                    <span className="inline-flex items-center rounded-full bg-indigo-100 px-2.5 py-1 text-sm font-medium text-indigo-700">
                      <TagIcon className="h-4 w-4 mr-1" />
                      {tag.name}
                    </span>
                  </div>
                  <div className="flex flex-none items-center gap-x-2">
                    <button
                      type="button"
                      onClick={() => handleOpenForm(tag)}
                      className="rounded-md bg-white px-2.5 py-1.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                      title="Edit"
                    >
                      <PencilIcon className="h-4 w-4 text-gray-500" aria-hidden="true" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(tag.id)}
                      className="rounded-md bg-white px-2.5 py-1.5 text-sm font-semibold text-red-600 shadow-sm ring-1 ring-inset ring-red-300 hover:bg-red-50"
                      title="Delete"
                    >
                      <TrashIcon className="h-4 w-4" aria-hidden="true" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Add/Edit Form Modal */}
      {isFormOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={handleCloseForm} />
            <div className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
              <div>
                <h3 className="text-lg font-semibold leading-6 text-gray-900">
                  {selectedTag ? 'Edit Tag' : 'Add New Tag'}
                </h3>
                <form onSubmit={handleSave} className="mt-4 space-y-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                      Tag Name
                    </label>
                    <input
                      type="text"
                      name="name"
                      id="name"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      placeholder="Enter tag name"
                    />
                  </div>

                  {error && (
                    <div className="rounded-md bg-red-50 p-4">
                      <p className="text-sm text-red-700">{error}</p>
                    </div>
                  )}

                  <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                    <button
                      type="submit"
                      disabled={loading}
                      className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50 sm:col-start-2"
                    >
                      {loading ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      type="button"
                      onClick={handleCloseForm}
                      className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:col-start-1 sm:mt-0"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageStatuses;
